import { Mail, Phone, MapPin, ShieldCheck, Sparkles } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function KontaktDe() {
  const kontaktSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Kontakt Danshaker',
    description: 'Kontaktieren Sie Danshaker für persönliche Beratung, Fragen oder Buchung einer kostenlosen Vorführung.',
    url: 'https://danshaker.dk/de/kontakt',
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Kontakt - Danshaker ApS"
        description="Kontaktieren Sie Danshaker in Randers, Dänemark. Rufen Sie uns an unter +45 2530 3589 oder schreiben Sie an info@danshaker.dk für Beratung oder Demobuchung."
        path="/de/kontakt"
        schema={kontaktSchema}
        lang="de"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Main Content */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-24 md:px-8 md:pt-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-40">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Wir stehen Ihnen gerne zur Verfügung</span>
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                Kontaktieren Sie Danshaker
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Haben Sie Fragen zur Maschine, zu Zubehör, Leasing, Miete oder möchten Sie eine unverbindliche Vorführung vereinbaren? Melden Sie sich jederzeit bei uns.
              </p>
            </div>

            {/* Contact Details Card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-6 shadow-xl">
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Adresse</span>
                    <span className="text-base font-semibold text-slate-900">
                      Tebbestrupvej 72<br />
                      8940 Randers SV, Dänemark
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Telefon</span>
                    <a
                      href="tel:25303589"
                      className="text-base font-semibold text-slate-900 transition hover:text-[#0E67A2]"
                    >
                      +45 2530 3589
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">E-Mail</span>
                    <a
                      href="mailto:info@danshaker.dk"
                      className="text-base font-semibold text-slate-900 transition hover:text-[#0E67A2]"
                    >
                      info@danshaker.dk
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4 pt-2 border-t border-slate-100">
                  <ShieldCheck className="h-6 w-6 text-slate-400 shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Unternehmensdaten</span>
                    <span className="text-sm text-slate-600 font-medium">
                      Danshaker ApS<br />
                      USt-IdNr. (CVR): DK40656093
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Image of Rasmus */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/f33352be-916e-41a6-8683-b6a80cadb435.jpg"
                alt="Rasmus Gangsted-Rasmussen - Inhaber von Danshaker"
                width="600"
                height="400"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
              <div className="p-4 text-center">
                <p className="font-bold text-slate-900">Rasmus Gangsted-Rasmussen</p>
                <p className="text-xs text-slate-500">Inhaber</p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12 shadow-2xl">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Schreiben Sie uns eine Nachricht
              </h2>
              <p className="text-slate-600 mb-8 font-medium">
                Füllen Sie einfach das Formular aus. Wir melden uns schnellstmöglich bei Ihnen – in der Regel innerhalb von 24 Stunden.
              </p>
              <ContactForm source="Kontaktseite (DE)" buttonText="Nachricht absenden" lang="de" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
